'use client';

import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useMediaSource, type MediaItem } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { SectionShell } from '../_shared/SectionShell';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { EmptyState } from '../_shared/EmptyState';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';

export interface VideoModuleGridProps {
  title?: string;
  source?: string;
  limit?: number;
  videoHrefTemplate?: string;
  /** When true, read {@code ?q=} from the URL and run the text search
   *  instead of the source feed (search-results pages). Falls back to
   *  the source feed when q is empty. */
  bindQueryParam?: boolean;
}

function useUrlParam(name: string): string | undefined {
  // useSearchParams (not window.location) — identical value on the server
  // pass and the client's first render, so no hydration mismatch, and it
  // re-reads on client-side navigation.
  const params = useSearchParams();
  return params?.get(name) ?? undefined;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function VideoModuleGrid({
  title,
  source: rawSource,
  limit: rawLimit,
  videoHrefTemplate = '/tube/{slug}',
  bindQueryParam = false,
}: VideoModuleGridProps) {
  const media = useMediaSource();
  const source = (rawSource ?? '').trim() || 'latest';
  const limit = clamp(
    typeof rawLimit === 'number' && Number.isFinite(rawLimit) ? rawLimit : 24,
    1,
    60,
  );
  const urlQ = useUrlParam('q');
  const q = bindQueryParam ? (urlQ ?? '').trim() : '';

  const state = useAsyncResource<MediaItem[]>(
    async (signal) => {
      if (!media) return null;
      if (q) {
        const res = await media.videos.fetchByCategory({ q, limit, signal });
        // MediaVideoFull → lean MediaItem (posterUrl → imageUrl).
        const items = res.items.map(
          (v): MediaItem => ({ id: v.id, slug: v.slug, title: v.title, imageUrl: v.posterUrl }),
        );
        return items.length > 0 ? items : null;
      }
      const res = await media.videos.fetchSource({ source, limit, signal });
      return res && res.items.length > 0 ? res.items : null;
    },
    [media, source, limit, q],
  );

  const effectiveTitle = q ? `Results for “${q}”` : title;

  if (!media) {
    return (
      <SectionShell title={effectiveTitle}>
        <AdapterPlaceholder blockType="VideoModuleGrid" adapterName="MediaSource" />
      </SectionShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <SectionShell title={effectiveTitle}>
        <SkeletonRow count={limit} layout="grid" />
      </SectionShell>
    );
  }

  if (state.kind !== 'loaded') {
    return (
      <SectionShell title={effectiveTitle}>
        <EmptyState
          title={q ? 'No results' : 'No videos'}
          hint={q ? 'Try a different search term.' : 'Videos will appear here when published.'}
          diagnostic={q || source}
        />
      </SectionShell>
    );
  }

  return (
    <SectionShell title={effectiveTitle}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
          gap: 'clamp(12px, 2vw, 16px)',
        }}
      >
        {state.data.map((item) => (
          <Link
            key={item.id}
            href={videoHrefTemplate.replace('{slug}', encodeURIComponent(item.slug))}
            style={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div
              aria-hidden
              style={{
                aspectRatio: '16 / 9',
                borderRadius: 'var(--radius-md, 8px)',
                background: item.imageUrl
                  ? `url(${item.imageUrl}) center/cover no-repeat`
                  : 'var(--color-neutral-200, #e4e4e7)',
              }}
            />
            <div
              style={{ fontSize: 'var(--font-size-sm, 14px)', fontWeight: 600, lineHeight: 1.3 }}
            >
              {item.title}
            </div>
          </Link>
        ))}
      </div>
    </SectionShell>
  );
}
